// =====================================================
// DASHBOARD STATS — Fenêtre de dates (source unique du pilotage)
// =====================================================
// RETOUCHE 1 : le dashboard n'est plus piloté par un preset de période
// (Semaine/Mois/Cumul/…) mais par une PLAGE DE DATES explicite [début, fin]
// choisie par l'utilisateur (date-picker). Toute la couche compute est câblée sur
// cette fenêtre. La comparaison N-1 = EXACTEMENT la même plage décalée d'un an
// (année-1), calculée par `shiftWindowYears(w, -1)` — plus de comparaison « brute
// même fenêtre par édition » de l'ancienne implémentation.

import type { DateWindow } from './types';

/**
 * Parse une date ISO courte 'yyyy-mm-dd' en Date LOCALE (00:00 local).
 * On évite `new Date('yyyy-mm-dd')` qui parse en UTC (décalage d'un jour selon
 * le fuseau) — on découpe les composantes et on construit une date locale.
 */
function parseLocalDate(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, (m ?? 1) - 1, d ?? 1);
}

/** Date → 'yyyy-mm-dd' (composantes LOCALES). */
export function toIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Construit une fenêtre inclusive à partir de deux dates 'yyyy-mm-dd' :
 * [début 00:00:00.000 local, fin 23:59:59.999 local]. La borne haute couvre toute
 * la journée de fin (sinon les lignes du dernier jour seraient exclues).
 */
export function windowFromDates(startIso: string, endIso: string): DateWindow {
  const s = parseLocalDate(startIso);
  s.setHours(0, 0, 0, 0);
  const e = parseLocalDate(endIso);
  e.setHours(23, 59, 59, 999);
  return { start: s, end: e };
}

/**
 * Décale une fenêtre de `years` années civiles (N-1 = years = -1). On décale les
 * DEUX bornes de la même quantité calendaire (setFullYear) → « exactement la même
 * plage [début, fin] décalée d'un an », conforme à la RETOUCHE 1.
 */
export function shiftWindowYears(w: DateWindow, years: number): DateWindow {
  const s = new Date(w.start.getTime());
  s.setFullYear(s.getFullYear() + years);
  const e = new Date(w.end.getTime());
  e.setFullYear(e.getFullYear() + years);
  return { start: s, end: e };
}

/**
 * Fenêtre par défaut du date-picker : les 3 derniers mois jusqu'à `now` (inclus).
 * Choix d'un défaut qui MONTRE des données (le seed STATS-DEMO peuple cette plage
 * + la même plage année-1 pour le N-1). Retourne des dates 'yyyy-mm-dd'.
 */
export function defaultDateWindow(now: Date): { startIso: string; endIso: string } {
  const start = new Date(now.getTime());
  start.setMonth(start.getMonth() - 3);
  return { startIso: toIsoDate(start), endIso: toIsoDate(now) };
}

/** Une date ISO tombe-t-elle dans la fenêtre [start, end] (inclusive) ? */
export function inWindow(iso: string | null, w: DateWindow): boolean {
  if (!iso) return false;
  const t = new Date(iso).getTime();
  return t >= w.start.getTime() && t <= w.end.getTime();
}
